import { NavigationProp, ParamListBase, useIsFocused, useNavigation } from "@react-navigation/native";
import React from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputProps,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native";

import { tBrandRetailerConnectionResponse } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import { useAppDispatch } from "src/redux";
import * as utilsDb from "src/db/utils.db";
import * as connectionDb from "src/db/connection.db";
import { PdStack, PdText } from "src/components/layout";
import { useDebounce, useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

import { useSelector } from "react-redux";
import { tPressableState } from "src/components/types";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdMessage, PdSearch } from "src/components/Icons";
import { formatDatabaseConnectionStatus } from "tmt_api/src/util/formatters";
import { kTablePageSize } from "src/util/constants";

import {
  changeFilterConnectionStatus,
  changeFilterNextPage,
  changeFilterPrevPage,
  changeFilterTerm,
  resetFilters,
} from "src/redux/filters/actions";
import { PdTablePaginationFooter } from "src/components/layout/table";
import { Carousel } from "src/components/ui";
import { formatMoney } from "src/util/gFormatting";
import { kRoute } from "src/kRoute";
import { PdIconButton } from "src/components/forms/icon-button";
import { setCurrentThread } from "src/redux/messages/actions";
import { useRetailerAccountToMessageModal } from "src/hooks/modals";
import { PdTab, PdTabLabel } from "src/components/v2/layout";
import { PdFormLabel } from "src/components/v2/forms/control";

type tTableState = {
  data: tBrandRetailerConnectionResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const PdSearchInput = React.forwardRef<TextInput, TextInputProps & { contentContainerStyle?: StyleProp<TextStyle> }>(
  ({ contentContainerStyle, style, ...rest }, ref) => {
    return (
      <View style={[styles.pdSearchInputContainer, style]}>
        <View style={styles.pdSearchInputIconContainer}>
          <PdSearch style={styles.pdSearchInputIcon} />
        </View>
        <TextInput
          style={[styles.pdSearchInputTextInput, contentContainerStyle]}
          placeholderTextColor={kColors.gray500}
          ref={ref}
          {...rest}
        />
      </View>
    );
  },
);

const BrandCard: React.FC<{ item: tBrandRetailerConnectionResponse; style?: StyleProp<ViewStyle> }> = ({
  item,
  style,
}) => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const retailerAccountToMessageModal = useRetailerAccountToMessageModal();
  const rMessagesThreads = useSelector((state) => state.MessageThreadsReducer.rThreads);

  const handleOnPressShopBrand = () => {
    navigation.navigate(kRoute.P100, { brand: item.brandName });
  };

  const handleOnPressMessage = () => {
    if (item.repId) {
      retailerAccountToMessageModal.openAsync({ repId: item.repId, brandId: item.brandId });
      return;
    }

    try {
      const rThreadId = rMessagesThreads.find((thread) => thread.recipient.includes(item.brandId));
      if (!rThreadId) throw new Error("An error occurred while attempting to parse the recipient's id.");
      dispatch(setCurrentThread(rThreadId));
      navigation.navigate(kRoute.P04);
    } catch (er) {
      dispatch({
        type: "RIBBON",
        message: new Error("Please use the left menu to send a message to the Retailer"),
      });
    }
  };

  return (
    <Pressable style={style}>
      {({ hovered }: tPressableState) => {
        return (
          <View style={hovered ? styles.hoveredBrandCard : styles.brandCard}>
            <TouchableOpacity onPress={handleOnPressShopBrand}>
              <Carousel
                style={styles.brandCarousel}
                mobileStyle={styles.brandImage}
                webStyle={webStyles.brandImage}
                imageUrls={item.brandImages}
              />
            </TouchableOpacity>
            <View style={styles.brandCardContent}>
              <View style={styles.brandCardTitleContainer}>
                <PdText style={styles.brandCardTitle}>{item.brandName}</PdText>
                <PdIconButton
                  onPress={handleOnPressMessage}
                  size="sm"
                  colorScheme="pink"
                  style={styles.brandCardMessageButton}
                  contentContainerStyle={styles.brandCardMessageButtonContent}
                  icon={<PdMessage style={styles.brandCardMessageButtonIcon} />}
                />
              </View>

              <PdText style={styles.brandCardSubtitle}>{`Opening Min. ${formatMoney(
                item.brandOpeningMinimum,
              )}`}</PdText>
              <PdText style={styles.brandCardSubtitle}>{`Reorder Min. ${formatMoney(
                item.brandReorderMinimum,
              )}`}</PdText>
              <PdText style={styles.brandCardSubtitle}>{`SHIPPING: ${item.shipping}`}</PdText>
              <PdText style={styles.brandCardSubtitleEnd}>{`SALES REP: ${item.repName}`}</PdText>
              <PdButton onPress={handleOnPressShopBrand} style={styles.brandCardShopButton} variant="outline">
                <PdButtonLabel>Shop Brand</PdButtonLabel>
              </PdButton>
            </View>
          </View>
        );
      }}
    </Pressable>
  );
};

const useGridLayout = () => {
  const { width } = useWindowDimensions();
  let numColumns = 5;
  if (width >= 1600) numColumns = 4;
  if (width >= 1200 && width < 1600) numColumns = 3;
  if (width >= 600 && width < 1200) numColumns = 2;
  if (width < 600) numColumns = 1;
  return numColumns;
};

const GAP = 30;

const P21_RetailerMyBrands: React.FC<{ navigation: NavigationProp<ParamListBase> }> = () => {
  const numColumns = useGridLayout();
  const isFocused = useIsFocused();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const filters = useSelector((state) => state.FiltersReducer.retailersMyBrands);
  const dTermFilter = useDebounce(filters.term, 500);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "retailersMyBrands", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "retailersMyBrands", value: tableState?.next ?? "" }));
  };

  const renderItem: React.FC<{ item: tBrandRetailerConnectionResponse }> = React.useCallback(
    ({ item }) => {
      return <BrandCard item={item} style={{ flex: 1 / numColumns, margin: GAP / 2 }} />;
    },
    [numColumns],
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const query = await utilsDb.withPagination(
        connectionDb.queryBrandRetailerConnection,
        connectionDb.documentBrandRetailerConnection,
        {
          retailerId: rCompanyId,
          term: dTermFilter,
          status: filters.connectionStatus ? formatDatabaseConnectionStatus(filters.connectionStatus) : undefined,
          prev: filters.prev,
          next: filters.next,
          limit: kTablePageSize,
        },
      );

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapBrandRetailerConnection(doc.data()));
        setTableState({
          isLoading: false,
          data,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [dTermFilter, filters.connectionStatus, filters.next, filters.prev, rCompanyId]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  if (tableState.isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={isMobile ? styles.mobileContainer : styles.webContainer}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={styles.headerContainer}>
        <PdText variant="subheader" style={isMobile ? styles.mb4 : {}}>
          My Brands
        </PdText>
      </PdStack>
      <View style={styles.mb4}>
        <PdFormLabel>Search your brands</PdFormLabel>
        <PdSearchInput
          value={filters.term}
          onChangeText={(value) => dispatch(changeFilterTerm({ pageName: "retailersMyBrands", value }))}
          placeholder="Search Customers"
        />
      </View>
      <View style={styles.filtersContainer}>
        <PdTab
          isActive={!filters.connectionStatus}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "retailersMyBrands" }))}
        >
          <PdTabLabel>All</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Active"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "retailersMyBrands", value: "Active" }))}
        >
          <PdTabLabel>Active</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Pending"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "retailersMyBrands", value: "Pending" }))}
        >
          <PdTabLabel>Pending</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Invited"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "retailersMyBrands", value: "Invited" }))}
        >
          <PdTabLabel>Invited</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Declined"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "retailersMyBrands", value: "Declined" }))}
        >
          <PdTabLabel>Declined</PdTabLabel>
        </PdTab>
      </View>
      <FlatList
        numColumns={numColumns}
        data={tableState.data}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        style={{ marginHorizontal: -(GAP / 2) }}
      />
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </ScrollView>
  );
};

export const P21 = React.memo(P21_RetailerMyBrands);

const styles = StyleSheet.create({
  brandCard: {
    borderRadius: kStyleConst.kSpacing,
    height: "100%",
  },
  brandCardContent: {
    flex: 1,
    padding: 4 * kStyleConst.kSpacing,
  },
  brandCardMessageButton: {
    borderRadius: 999,
    marginLeft: "auto",
  },
  brandCardMessageButtonContent: {
    borderRadius: 999,
  },
  brandCardMessageButtonIcon: {
    height: 15,
    width: 15,
  },
  brandCardShopButton: {
    marginRight: "auto",
    marginTop: "auto",
  },
  brandCardSubtitle: {
    fontSize: 12,
    lineHeight: 20,
  },
  brandCardSubtitleEnd: {
    fontSize: 12,
    lineHeight: 20,
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  brandCardTitle: {
    fontWeight: "500",
  },
  brandCardTitleContainer: {
    flexDirection: "row",
  },
  brandCarousel: {
    borderRadius: kStyleConst.kSpacing,
  },
  brandImage: {
    backgroundColor: kColors.gray100,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    height: 300,
  },
  filtersContainer: {
    alignItems: "center",
    backgroundColor: kColors.white,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: kStyleConst.kSpacing * 2,
    width: "100%",
  },
  headerContainer: {
    justifyContent: "space-between",
    marginBottom: kStyleConst.kSpacing * 4,
  },
  hoveredBrandCard: {
    backgroundColor: kColors.gray50,
    borderRadius: kStyleConst.kSpacing,
    height: "100%",
  },
  loadingContainer: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobileContainer: {
    paddingBottom: kStyleConst.kSpacing * 2,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },

  pdSearchInputContainer: {
    alignItems: "center",
    borderColor: kColors.grayPink,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    flexDirection: "row",
    paddingHorizontal: kStyleConst.kSpacing * 2,
  },
  pdSearchInputIcon: {
    color: kColors.brand500,
    height: 16,
    width: 16,
  },
  pdSearchInputIconContainer: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100%",
    paddingRight: kStyleConst.kSpacing * 2,
  },
  pdSearchInputTextInput: {
    color: kColors.brand500,
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: "300",
    height: 34,
    letterSpacing: 1.2,
    width: "100%",
  },
  webContainer: {
    marginBottom: kStyleConst.kSpacing * 8,
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingTop: kStyleConst.kSpacing * 8,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  brandImage: {
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    height: 300,
  },
} as const;
